import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { MessageSquare, Users, Building2 } from 'lucide-react';

interface TopTenant {
  id: string;
  name: string;
  slug: string;
  plan: string;
  status: string;
  messageCount: number;
  conversationCount: number;
  agentCount: number;
}

interface TopTenantsTableProps {
  data: TopTenant[];
  isLoading?: boolean;
}

function getStatusBadgeVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'active': return 'default';
    case 'trial': return 'secondary';
    case 'suspended': return 'outline';
    case 'cancelled': return 'destructive';
    default: return 'outline';
  }
}

function getPlanBadgeStyle(plan: string): string {
  switch (plan.toLowerCase()) {
    case 'enterprise': return 'bg-purple-500/10 text-purple-500 border-purple-500/20';
    case 'professional': return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
    case 'starter': return 'bg-green-500/10 text-green-500 border-green-500/20';
    default: return 'bg-muted text-muted-foreground';
  }
}

function formatNumber(num: number): string {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
}

export function TopTenantsTable({ data, isLoading }: TopTenantsTableProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-5 w-5" />
          Top Clientes por Volume
        </CardTitle>
        <CardDescription>Clientes com maior volume de mensagens</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center gap-3">
                <Skeleton className="h-4 w-4" />
                <Skeleton className="h-4 w-32 flex-1" />
                <Skeleton className="h-6 w-16" />
                <Skeleton className="h-6 w-12" />
              </div>
            ))}
          </div>
        ) : data.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            Nenhum cliente cadastrado ainda.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40px]">#</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead className="text-center">Plano</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <MessageSquare className="h-3.5 w-3.5" />
                    Msgs
                  </div>
                </TableHead>
                <TableHead className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Users className="h-3.5 w-3.5" />
                    Agentes
                  </div>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((tenant, index) => (
                <TableRow key={tenant.id}>
                  <TableCell>
                    <span className={`font-bold ${
                      index === 0 ? 'text-yellow-500' :
                      index === 1 ? 'text-gray-400' :
                      index === 2 ? 'text-amber-600' :
                      'text-muted-foreground'
                    }`}>
                      {index + 1}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium truncate max-w-[200px]">{tenant.name}</p>
                      <p className="text-xs text-muted-foreground">{tenant.slug}</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant="outline" className={getPlanBadgeStyle(tenant.plan)}>
                      {tenant.plan}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant={getStatusBadgeVariant(tenant.status)}>
                      {tenant.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-mono font-semibold">
                    {formatNumber(tenant.messageCount)}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {tenant.agentCount}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
